import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import * as i18next from 'i18next';
import { G2S } from 'core/g2s';
import router from 'src/router';
import * as api from 'core/api';
import * as flights from 'core/flights';
import * as utils from 'core/utils';
import FlightAgencyTermsComponent from 'src/components/flight/agency-terms';
import OrderOpinionComponent from 'src/components/order/opinion';
import OrderViewSidebarComponent from './sidebar';
import { ReservationModel } from 'core/flights';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

@WithRender
@Component({
    components: {
        'v-order-opinion': OrderOpinionComponent,
        'v-order-view-sidebar': OrderViewSidebarComponent,
        'v-flight-agency-terms': FlightAgencyTermsComponent,
    }
})
export default class OrderViewComponent extends Vue {
    @Prop()
    orderGuid: string;

    flight: flights.FlightModel | null = null;

    gaParams: utils.GoogleAnalyticsParams = new utils.GoogleAnalyticsParams();
    gaAlreadySubmitted: string[] = [];

    pixelApplied: boolean = false;
    isWaitingSurcharge: boolean = false;
    isMaskedPersonalData: boolean = false;

    refreshInProgress: boolean = false;
    refreshAttemptCount: number = 1;

    reservation: api.ReservationInfo | null = null;

    cancelProductRequestsInProgress = {};
    cancelProductId: number = 0;
    cancelProductIndex: number = 0;

    title: string = i18next.t('title_order');

    view: string = 'loading';
    viewError: string = '';

    lastHistoryStateView: string = '';

    modalProductInfo = {
        buttonText1: 'Оставить услугу со скидкой 50%',
        text:  `<div>
                <div class="d-flex flex-row">
                    <h2>Как жаль, что вы отказываетесь от услуги <span>умный пакет</span></h2>
                </div>
                <div class="text mt-2 pl-0">
                    <p>Мы расстроены и дарим вам 50% скидки на заказ данной услуги</p>
                </div>
                </div>`
    };

    tooltipActivator: EventTarget | null = null;
    tooltipContent: string | null = null;

    selectedProduct: api.Product | any = {
        adapter: '',
        id: 0,
        name: '',
        description: '',
        cancelPossible: false,
        productReservationId: 0,
        type: '',
        status: ''
    };

    selectedProductIdsOnOrderPage: number[] = [];

    disabledModalButton: boolean = false;

    @Watch('$route')
    onRouteChanged() {
        this.loadModel();
    }

    get totalCostOnOrderPage() {
        return this.selectedProductIdsOnOrderPage.reduce((acc, id) => {
            const product = this.reservation!.productsOnOrderPage.products.find(p => p.id === id)!;
            acc += product.billingPrice;
            return acc;
        }, 0);
    }

    get productsPurchaseUrl() {
        const ct = utils.Cookies.getChoiceType() ?? api.ProductChoiceType.orderPageAccepted;
        const ids = this.selectedProductIdsOnOrderPage.join('_');
        return `/order/booking/product/${this.orderGuid}/${ids}?ct=${ct}`;
    }

    mounted() {
        // Redirect to mobile
        if (G2S.config.mobileRedirectAllowed) {
            window.location.href = G2S.config.mobileUrl + '/order/' + this.orderGuid;
            return;
        }

        window.history.replaceState({ ...window.history.state, view: 'view' }, '', router.currentRoute.fullPath);
        this.setLastHistoryStateView();
        window.addEventListener('popstate', this.onPopState);

        if (this.$route.query.ct) {
            utils.Cookies.setChoiceType(parseInt(<string>this.$route.query.ct));
        }

        this.loadModel();
    }

    beforeDestroy() {
        window.removeEventListener('popstate', this.onPopState);
    }

    setLastHistoryStateView() {
        this.lastHistoryStateView = window.history.state && window.history.state.view ? window.history.state.view : '';
    }

    onPopState(event) {
        if (event.state && event.state.view == 'flight' && this.lastHistoryStateView == 'view') {
            this.onFlightDetails();
        } else
        if (event.state && event.state.view == 'view' && this.lastHistoryStateView == 'flight') {
            this.onFlightDetailsBack();
        }
    }

    get orderPaymentUrl(): string {
        return '/order/payment/' + this.reservation!.reservationPublicId;
    }

    destroyed() {
        this.refreshAttemptCount = 999;
    }

    applyPixels() {
        if (this.reservation!.isPayed || this.reservation!.isTicketed) {
            if (!this.pixelApplied) {
                utils.PixelsService.setPixelForOrderConfirmation(document.getElementById('pixels')!, this.reservation!, this.flight);

                (<any>this).$ga.sendPageview('/flights/booking/confirmation', 'Flights / Booking / Confirmation', this.gaParams);

                this.pixelApplied = true;
            }
        }
    }

    getBookingClassLabel(bookingClass: string): string {
        return i18next.t('label_flight_class_' + bookingClass.toLowerCase());
    }

    getGenderIcon(passenger: api.PassengerInfo): string {
        return passenger.gender.toLowerCase();
    }

    getInsuranceLabel(insurance: api.InsurancePolicy): string {
        return i18next.t('label_insurance_police_with_number', <any>{ i: insurance.externalId });
    }

    getInsuranceUrl(insurance: api.InsurancePolicy): string {
        return insurance.url;
    }

    getTicketUrl(passenger: api.PassengerInfo): string {
        return ReservationModel.getTicketUrl(this.reservation!, passenger);
    }

    getAllTicketsUrl() {
        return ReservationModel.getAllTicketsUrl(this.reservation!);
    }

    getReceiptUrl() {
        return ReservationModel.getReceiptUrl(this.reservation!);
    }

    getSupportLink(value) {
        return `/support/new/${this.reservation!.reservationNumber}?t=${value}&email=${this.reservation!.contactEmail}`;
    }

    loadModel(loadingIndicator = true) {
        if (loadingIndicator) {
            this.view = 'loading';
        }

        this.title = i18next.t('title_order');
        window.document.title = this.title;

        document.getElementById('pixels')!.innerHTML = '';

        new api.ReservationService().getReservationStatus(this.orderGuid, false, false).then(
            (result: api.GetReservationStatusResult) => {
                this.reservation = result.reservationInfo;
                if (result.isMaskedPersonalData) {
                    this.isMaskedPersonalData = result.isMaskedPersonalData;
                }

                this.title = i18next.t('label_order_with_number',
                    <any>{ i: this.reservation.reservationNumber }
                );

                window.document.title = this.title;

                if (result.flight) {
                    this.flight = new flights.FlightModel(result.flight, result);

                    this.gaParams.dimension2 = this.flight.adapter + '_'; // Add underscore for regex filtering in GA
                    this.gaParams.dimension3 = this.flight?.allFlightAirlinesCode?.length ? this.flight.allFlightAirlinesCode.join('_') : 'none';
                }
                this.gaParams.metric3 = this.reservation.passengers.length;

                if (this.reservation.isStatusPending
                    && !this.reservation.reservedTillExpired) {
                    this.refreshInProgress = true;

                    setTimeout(() => this.refreshStatus(), 5000);
                } else {
                    this.applyPixels();
                }

                this.view = 'view';
            },
            (error) => {
                this.viewError = i18next.t('error_server_tryagain');

                if (error.response) {
                    if (error.response.status === 404) {
                        this.viewError = i18next.t('error_order_notfound');
                    }
                    console.log('XHR failed', error.response.status, error.response.data);
                } else {
                    console.log('XHR error', error.message);
                }

                this.view = 'error';
            }
        );
    }

    detailProduct(index: number) {
        this.cancelProductId = this.reservation!.products[index].productReservationId;
        this.cancelProductIndex = index;
        this.selectedProduct = this.reservation!.products[index];
        
        (<any>this).$ga.sendEvent('order.view', 'product.detail', `order.view.product.detail${this.selectedProduct.saleType ? '.' + this.selectedProduct.saleType : ''}${this.selectedProduct.kitId ? '.kit' + this.selectedProduct.kitId : ''}.id${this.selectedProduct.id}`.toLowerCase(), this.gaParams);

        if (this.selectedProduct.cancelPossible) {
            (<any>this).$ga.sendEvent('order.view', 'product.detail', `order.view.product.detail.btn.show.cancel${this.selectedProduct.saleType ? '.' + this.selectedProduct.saleType : ''}${this.selectedProduct!.kitId ? '.kit' + this.selectedProduct!.kitId : ''}.id${this.selectedProduct!.id}`.toLowerCase(), this.gaParams);
        }

        (<any>this.$refs).modalProduct.isActive = true;
    }

    detailProductOnSale(index: number) {
        this.cancelProductId = this.reservation!.productsOnOrderPage.products[index].productReservationId;
        this.cancelProductIndex = index;
        this.selectedProduct = this.reservation!.productsOnOrderPage.products[index];

        (<any>this.$refs).modalProduct.isActive = true;
    }

    cancelProduct(index: number) {
        const productReservationId = this.reservation!.products[index].productReservationId;
        this.cancelProductId = productReservationId;
        this.cancelProductIndex = index;
        const billingPrice = this.reservation!.products[index].billingPrice;
        const request: api.CancelProductRequest = {
            productReservationId
        };
        if(this.reservation!.products[index].couponDiscountAmount){
            this.reservation!.products[index].couponDiscountAmount = 0;
            this.reservation!.couponDiscountAmount = 0;
        }

        this.$set(this.cancelProductRequestsInProgress, `${productReservationId}`, true);
        new api.ReservationService().cancelProduct(request).then(
            (result: api.CancelProductResponse) => {
                (<any>this.$refs).modalProduct.isActive = false;
                this.$set(this.cancelProductRequestsInProgress, `${productReservationId}`, false);
                (<any>this).$ga.sendEvent('order.view', 'product.detail', `order.view.product.detail${this.selectedProduct!.saleType ? '.' + this.selectedProduct!.saleType : ''}.btn.cancel${this.selectedProduct!.kitId ? '.kit' + this.selectedProduct!.kitId : ''}.id${this.selectedProduct!.id}`.toLowerCase(), this.gaParams);

                if (result.success) {
                    (<any>this).$ga.sendEvent('order.view', 'product.detail', `order.view.product.detail.btn.success.cancel${this.selectedProduct.saleType ? '.' + this.selectedProduct.saleType : ''}${this.selectedProduct!.kitId ? '.kit' + this.selectedProduct!.kitId : ''}.id${this.selectedProduct!.id}`.toLowerCase(), this.gaParams);
                }

                if (result.popup) {
                    (<any>this.$root).$refs.dialogCatchUp.show({
                        text: result.popup.text,
                        btnOkLabel: result.popup.firstButtonText,
                        centerActions: true,
                        persistent: false,
                        btnOkHandler: () => {
                            this.cancelProductAfterPopup(true);
                        },
                        btnCloseHandler: () => {
                            this.cancelProductAfterPopup(false);
                        }
                    });
                    // (<any>this.$refs).modalProductDetails.isActive = true;
                    (<any>this).$ga.sendEvent('order.view', 'msg.hold', `order.view.msg.hold${this.selectedProduct!.saleType ? '.' + this.selectedProduct!.saleType : ''}${this.selectedProduct!.kitId ? '.kit' + this.selectedProduct!.kitId : ''}.id${this.selectedProduct!.id}`.toLowerCase(), this.gaParams);
                } else {
                    this.loadModel(false);

                    (<any>this.$root).$refs.dialogMessage.show({
                        title: '',
                        text: result.localizedMessage
                    });
                }
            },
            (error) => {
                this.$set(this.cancelProductRequestsInProgress, `${productReservationId}`, false);
                (<any>this.$root).$refs.dialogMessage.show({
                    title: i18next.t('error'),
                    text: (error.response.data && error.response.data.message) ? error.response.data.message : i18next.t('error_server_tryagain')
                });
            }
        );
    }

    cancelProductAfterPopup(applyDiscount: boolean) {
        const productReservationId = this.cancelProductId;
        const billingPrice = this.reservation!.products[this.cancelProductIndex].billingPrice;
        const index = this.cancelProductIndex;
        const productId = this.reservation!.products[index].id;

        const request: api.CancelProductAfterPopupRequest = {
            applyDiscount,
            productReservationId
        };

        if (!this.disabledModalButton) {
            this.disabledModalButton = true;
            new api.ReservationService().cancelProductAfterPopup(request).then(
                (result: any) => {
                    console.log((<any>this.$root).$refs.dialogCatchUp);
                    (<any>this.$root).$refs.dialogCatchUp.onHide();
                    (<any>this.$root).$refs.dialogCatchUp.onLoading(false);
                    (<any>this.$refs).modalProductDetails.isActive = false;
                    this.disabledModalButton = false;
                    this.$set(this.cancelProductRequestsInProgress, `${productReservationId}`, false);

                    if (!applyDiscount && result.success) {
                        (<any>this).$ga.sendEvent('order.view', 'product.detail', `order.view.product.detail.btn.success.cancel${this.selectedProduct.saleType ? '.' + this.selectedProduct.saleType : ''}${this.selectedProduct!.kitId ? '.kit' + this.selectedProduct!.kitId : ''}.id${this.selectedProduct!.id}`.toLowerCase());
                    }

                    if (result.product) {
                        (<any>this).$ga.sendEvent('order.view', 'msg.hold', `order.view.msg.hold${this.selectedProduct!.saleType ? '.' + this.selectedProduct!.saleType : ''}.btn.discount${this.selectedProduct!.kitId ? '.kit' + this.selectedProduct!.kitId : ''}.id${productId}`.toLowerCase(), this.gaParams);
                        this.reservation!.products[index].price = result.product.price;
                        this.reservation!.products[index].billingPrice = result.product.billingPrice;
                        (<any>this.$root).$refs.dialogMessage.show({
                            title: '',
                            text: result.localizedMessage
                        });
                    } else {
                        (<any>this).$ga.sendEvent('order.view', 'msg.hold', `order.view.msg.hold${this.selectedProduct!.saleType ? '.' + this.selectedProduct!.saleType : ''}.btn.not.discount${this.selectedProduct!.kitId ? '.kit' + this.selectedProduct!.kitId : ''}.id${productId}`.toLowerCase(), this.gaParams);
                        this.$delete(this.reservation!.products, index);
                        this.reservation!.amount = this.reservation!.amount - billingPrice;
                        (<any>this.$root).$refs.dialogMessage.show({
                            title: '',
                            text: result.localizedMessage
                        });
                    }
                },
                (error) => {
                    this.disabledModalButton = false;
                    (<any>this.$root).$refs.dialogMessage.show({
                        title: i18next.t('error'),
                        text: (error.response.data && error.response.data.message) ? error.response.data.message : i18next.t('error_server_tryagain')
                    });
                }
            );
        }
    }

    refreshStatus() {
        if (this.refreshAttemptCount > 30
            || this.reservation
                && (!this.reservation.isStatusPending
                || this.reservation.reservedTillExpired)) {
            this.refreshInProgress = false;
        }

        if (!this.refreshInProgress) {
            return;
        }

        console.log('Refresh status #' + this.refreshAttemptCount + '...');

        new api.ReservationService().getReservationStatus(this.orderGuid, false, true).then(
            (result: api.GetReservationStatusResult) => {
                if (this.reservation!.reservationStatus !== result.reservationInfo.reservationStatus
                    || this.reservation!.uiStatusType !== result.reservationInfo.uiStatusType) {
                    this.reservation = result.reservationInfo;

                    this.applyPixels();
                } else {
                    this.reservation!.surchargeInfo = result.reservationInfo.surchargeInfo;
                }

                if (this.refreshInProgress) {
                    setTimeout(() => this.refreshStatus(), 10000 * this.refreshAttemptCount);
                }
            },
            (error) => {
                if (error.response) {
                    console.log('XHR failed', error.response.status, error.response.data);
                } else {
                    console.log('XHR error', error.message);
                }
            }
        );

        this.refreshAttemptCount++;
    }

    showSurchargeModal() {
        const reservationService = new api.ReservationService();
        const request: api.StartedReservation = {
            reservationPublicId: this.reservation!.reservationPublicId
        };

        if (this.reservation && this.reservation!.surchargeInfo) {
            const surchargeInfo = this.reservation!.surchargeInfo;
            (<any>this.$root).$refs.dialogMessage.show({
                text: surchargeInfo.clientText,
                btnOkLabel: surchargeInfo.buttonAcceptText,
                btnCancelLabel: surchargeInfo.buttonRejectText,
                btnCancelColorClass: 'btn-default',
                persistent: true,
                btnOkHandler: () => {
                    (<any>this).$ga.sendEvent(
                        'flights.booking',
                        'msg.price.rise',
                        'flights.booking.msg.price.rise.ok',
                        this.gaParams
                    );
                    this.isWaitingSurcharge = true;
                    reservationService.CompleteStartedReservation(request)
                        .then(() => {
                            this.refreshStatus();
                        },
                        (error) => {
                            this.isWaitingSurcharge = false;
                            (<any>this.$root).$refs.dialogMessage.show({
                                title: i18next.t('error'),
                                text: (error.response.data && error.response.data.message) ? error.response.data.message : i18next.t('error_server_tryagain')
                            });
                        });
                },
                btnCancelHandler: () => {
                    (<any>this).$ga.sendEvent(
                        'flights.booking',
                        'msg.price.rise',
                        'flights.booking.msg.price.rise.search',
                        this.gaParams
                    );
                    this.isWaitingSurcharge = true;
                    reservationService.RejectStartedReservation(request)
                        .then(() => {
                            this.refreshStatus();
                        },
                        (error) => {
                            this.isWaitingSurcharge = false;
                            (<any>this.$root).$refs.dialogMessage.show({
                                title: i18next.t('error'),
                                text: (error.response.data && error.response.data.message) ? error.response.data.message : i18next.t('error_server_tryagain')
                            });
                        });
                }
            });
        }
    }

    onFlightDetails() {
        this.setLastHistoryStateView();
        this.view = 'flight';
    }

    onFlightDetailsClick() {
        window.history.pushState({ ...window.history.state, view: 'flight' }, '', router.currentRoute.fullPath);
        this.onFlightDetails();
    }

    onFlightDetailsBack() {
        this.setLastHistoryStateView();
        this.view = 'view';
    }

    onFlightDetailsBackClick() {
        window.history.go(-1);
    }

    onFlightTerms() {
        if (this.reservation) {
            (<any>this).$refs.flightTerms.show(null, null, this.reservation.reservationPublicId);
        }
    }

    supportEvent() {
        (<any>this).$ga.sendEvent('support.new', 'click', 'support.new.click');
        (<any>this).$ga.reachGoal('support.new.click');
        (<any>this).$ga.reachGoal('support.new.click.order');
    }

    onReturnTicketClick() {
        (<any>this).$ga.sendEvent('refund.tickets', 'click', 'refund.tickets.click.order', this.gaParams);
        this.supportEvent();
    }

    isTextStrikeNeeded(product: api.Product) {
        return product.status == api.ProductStatus.canceled
            || product.status == api.ProductStatus.deleted
            || product.status == api.ProductStatus.refunded;
    }

    crutchName(product: api.Product) { // Костыль на время тестов
        let newName = '';
        switch (product.kitId) {
            case 236:
                newName = 'Умный пакет';
                break;
            case 237:
                newName = 'Гарантия возврата билета в случае болезни';
                break;
            case 238:
                newName = 'Гарантированный возврат';
                break;
            default:
                newName = product.name;
                break;
        }

        return newName;
    }

    onTooltipTargetMouseEnter(event: MouseEvent) {
        this.tooltipActivator = event.target;
        this.tooltipContent = 'Для просмотра данных пассажира необходимо авторизоваться';
        (<any>this.$refs).tooltip.isActive = true;
    }

    onTooltipTargetMouseLeave() {
        this.tooltipActivator = null;
        (<any>this.$refs).tooltip.isActive = false;
    }
}
